import React, { useState, useEffect } from 'react';
import { X, Heart, Activity, Zap, TrendingUp } from 'lucide-react';
import { patientsAPI } from '../services/api/api';
import useEscKeyHandler from '../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';

const PatientVitalsModal = ({ patient, onClose }) => {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVitals();
  }, [patient.id]);

  // Add Esc key handler for modal
  useEscKeyHandler(true, onClose);

  const loadVitals = async () => {
    try {
      const response = await patientsAPI.getPatientVitals(patient.id);
      setVitals(response.data);
    } catch (error) {
      toast.error('Failed to load patient vitals');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getLatestVitals = () => {
    return vitals.length > 0 ? vitals[0] : null;
  };

  const latest = getLatestVitals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg mr-3">
              <Heart className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{patient.alias} - Vital Signs</h2>
              <p className="text-sm text-gray-500">Patient ID: {patient.id.substring(0, 8)}...</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading vitals...</p>
            </div>
          ) : vitals.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No vital signs recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Latest Vitals Summary */}
              {latest && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Latest Reading</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="p-3 bg-red-100 rounded-full inline-block mb-2">
                        <Heart className="h-6 w-6 text-red-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{latest.heart_rate || 'N/A'}</p>
                      <p className="text-sm text-gray-600">Heart Rate (BPM)</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="p-3 bg-blue-100 rounded-full inline-block mb-2">
                        <Activity className="h-6 w-6 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {latest.heart_rate_variability ? latest.heart_rate_variability.toFixed(1) : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">HRV (ms)</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="p-3 bg-purple-100 rounded-full inline-block mb-2">
                        <Zap className="h-6 w-6 text-purple-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">{latest.rhythm || 'Normal'}</p>
                      <p className="text-sm text-gray-600">Rhythm</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="p-3 bg-green-100 rounded-full inline-block mb-2">
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      </div>
                      <p className="text-lg font-bold text-gray-900">
                        {latest.blood_pressure_systolic && latest.blood_pressure_diastolic 
                          ? `${latest.blood_pressure_systolic}/${latest.blood_pressure_diastolic}`
                          : 'N/A'
                        }
                      </p>
                      <p className="text-sm text-gray-600">Blood Pressure</p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">
                      Recorded on {formatDate(latest.created_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Vitals History */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vitals History</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date/Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Heart Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          HRV
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rhythm
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Blood Pressure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quality
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vitals.map((vital) => (
                        <tr key={vital.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-va-neutral-900">
                            {formatDate(vital.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-va-neutral-900">
                            {vital.heart_rate ? `${vital.heart_rate} BPM` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-va-neutral-900">
                            {vital.heart_rate_variability ? `${vital.heart_rate_variability.toFixed(1)} ms` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-va-neutral-900">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              vital.rhythm === 'Normal' 
                                ? 'bg-va-health-100 text-va-health-800'
                                : vital.rhythm === 'Irregular'
                                ? 'bg-va-warning-100 text-va-warning-800'
                                : 'bg-va-error-100 text-va-error-800'
                            }`}>
                              {vital.rhythm || 'Normal'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-va-neutral-900">
                            {vital.blood_pressure_systolic && vital.blood_pressure_diastolic
                              ? `${vital.blood_pressure_systolic}/${vital.blood_pressure_diastolic}`
                              : 'N/A'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              vital.quality_flag 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {vital.quality_flag ? 'Good' : 'Poor'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientVitalsModal;